import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { ApiRequestError } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"

import {
  browseStorage,
  createFolderInStorage,
  createStorageFolderLink,
  renameFolderInStorage,
  setStorageFolder,
  uploadFileToStorage,
  type StorageRecordType,
} from "./api"

// The detail query key that holds each record's `storage_folder_id`, so linking
// a folder refreshes the detail page.
const RECORD_DETAIL_KEY: Record<StorageRecordType, string> = {
  households: "household",
  contacts: "contact",
  businesses: "business",
}

export const storageKeys = {
  browse: (recordType: StorageRecordType, recordId: string, folderId?: string) =>
    [recordType, "detail", recordId, "storage", "browse", folderId] as const,
  browseRoot: (recordType: StorageRecordType, recordId: string) =>
    [recordType, "detail", recordId, "storage", "browse"] as const,
}

export function storageMutationErrorMessage(error: Error) {
  const body = error instanceof ApiRequestError ? error.responseBody : undefined
  if (typeof body === "object" && body !== null && "message" in body && typeof body.message === "string") {
    return body.message
  }
  return error.message
}

export function useStorageBrowse(
  recordType: StorageRecordType,
  recordId: string,
  folderId?: string,
  options?: { enabled?: boolean }
) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: storageKeys.browse(recordType, recordId, folderId),
    queryFn: async ({ signal }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return browseStorage(context, recordType, recordId, folderId, signal)
    },
    enabled: !!recordId && options?.enabled !== false,
    // A stale/missing folder returns a 4xx (e.g. storage_folder_not_found). Don't retry
    // those — surface the error immediately so the browser can stop loading.
    retry: (failureCount, error) => {
      if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) return false
      return failureCount < 3
    },
  })
}

export function useSetStorageFolder(recordType: StorageRecordType, recordId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    meta: { errorToast: storageMutationErrorMessage },
    mutationFn: async (folderId: string | null) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return setStorageFolder(context, recordType, recordId, folderId)
    },
    onSuccess: (_data, folderId) => {
      // Refresh the record detail to pick up the new storage_folder_id.
      queryClient.invalidateQueries({ queryKey: [RECORD_DETAIL_KEY[recordType], recordId] })
      // Refresh all storage browse queries for this record.
      queryClient.invalidateQueries({ queryKey: storageKeys.browseRoot(recordType, recordId) })
      if (folderId) {
        queryClient.invalidateQueries({ queryKey: storageKeys.browse(recordType, recordId, folderId) })
      }
    },
  })
}

// Mints a fresh provider link for a folder (e.g. ShareFile one-time login URIs).
export function useCreateStorageFolderLink(recordType: StorageRecordType, recordId: string) {
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async (folderId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createStorageFolderLink(context, recordType, recordId, folderId)
    },
    meta: { errorToast: false },
  })
}

export function useUploadFileToStorage(
  recordType: StorageRecordType,
  recordId: string,
  folderId?: string,
  subfolder?: string
) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    meta: { errorToast: storageMutationErrorMessage },
    mutationFn: async (file: File) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return uploadFileToStorage(context, recordType, recordId, file, folderId, subfolder)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: storageKeys.browse(recordType, recordId, folderId) })
    },
  })
}

export function useCreateFolderInStorage(recordType: StorageRecordType, recordId: string, parentFolderId?: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    meta: { errorToast: storageMutationErrorMessage },
    mutationFn: async (folderName: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createFolderInStorage(context, recordType, recordId, folderName, parentFolderId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: storageKeys.browse(recordType, recordId, parentFolderId) })
    },
  })
}

export function useRenameFolderInStorage(recordType: StorageRecordType, recordId: string, parentFolderId?: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    meta: { errorToast: storageMutationErrorMessage },
    mutationFn: async ({ folderId, newName }: { folderId: string; newName: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return renameFolderInStorage(context, recordType, recordId, folderId, newName)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: storageKeys.browse(recordType, recordId, parentFolderId) })
    },
  })
}
